import { Component, Inject, type OnInit } from '@angular/core'
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions, MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog'
import { UserService } from '@inside-hub-app/customer-portal-b2c-client'
import { SharedTranslatePipe, TransifexService } from '@inside-hub-app/customer-portal-shared'
import { type CustomerPortalConfig } from '@inside-hub-app/customer-portal-config'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { InstantErrorStateMatcherService } from '../../../services/instant-error-state-matcher.service'
import { SharedService } from '../../../services/shared.service'
import {
  FrequentDriverDialogData,
  type VehicleFrequentDriverDTO,
  VehiclesService
} from '../../../services/vehicles.service'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'

import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input'
import { MatSelect } from '@angular/material/select'
import { MatOption } from '@angular/material/core'
import { MatDivider } from '@angular/material/list'
import { MatButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatProgressSpinner } from '@angular/material/progress-spinner'

@Component({
  selector: 'customer-portal-app-frequent-driver-popup',
  standalone: true,
  imports: [
    HubChildTranslateModule,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    SharedTranslatePipe,
    MatSelect,
    MatLabel,
    MatOption,
    MatInput,
    MatError,
    MatDivider,
    MatDialogActions,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatDialogTitle,
    MatDialogClose
  ],
  templateUrl: './frequent-driver-popup.component.html'
})
export class FrequentDriverPopupComponent implements OnInit {
  matcher = new InstantErrorStateMatcherService()
  salutations
  salutationFirstNameMaxLength: number
  salutationLastNameMaxLength: number

  mostFrequentDriverFirstNamePattern: string
  mostFrequentDriverLastNamePattern: string
  frequentDriverForm
  saving: boolean
  country: string

  constructor (
    private readonly dialog: MatDialog,
    private readonly vehicleService: VehiclesService,
    private readonly userService: UserService,
    private readonly dialogRef: MatDialogRef<FrequentDriverPopupComponent>,
    public sharedService: SharedService,
    public transifexService: TransifexService,
    @Inject(MAT_DIALOG_DATA) public data: FrequentDriverDialogData,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.userService.enums().subscribe(value => {
      this.salutations = value.salutations
    })
    this.salutationFirstNameMaxLength = this.remoteConfigService.get(
      'regEx.mostFrequentDriverRegEx.firstName.maxLength'
    )
    this.salutationLastNameMaxLength = this.remoteConfigService.get(
      'regEx.mostFrequentDriverRegEx.lastName.maxLength'
    )
    this.mostFrequentDriverFirstNamePattern = this.remoteConfigService.get(
      'regEx.mostFrequentDriverRegEx.firstName.pattern'
    )
    this.mostFrequentDriverLastNamePattern = this.remoteConfigService.get(
      'regEx.mostFrequentDriverRegEx.lastName.pattern'
    )

    this.frequentDriverForm = new FormGroup({
      firstName: new FormControl<string | null>(
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(this.mostFrequentDriverFirstNamePattern),
          Validators.maxLength(
            this.remoteConfigService.get(
              'regEx.mostFrequentDriverRegEx.firstName.maxLength'
            )
          )
        ])
      ),
      lastName: new FormControl<string | null>(
        null,
        Validators.compose([
          Validators.required,
          Validators.pattern(this.mostFrequentDriverLastNamePattern),
          Validators.maxLength(
            this.remoteConfigService.get(
              'regEx.mostFrequentDriverRegEx.lastName.maxLength'
            )
          )
        ])
      ),
      salutation: new FormControl<string | null>(null, Validators.required)
    })
  }

  ngOnInit (): void {
    this.country = this.remoteConfigService.get('country.code')
    if (this.data.mostFrequentDriver != null) {
      this.frequentDriverForm.controls.firstName.setValue(
        this.data.mostFrequentDriver.firstName
      )
      this.frequentDriverForm.controls.lastName.setValue(
        this.data.mostFrequentDriver.lastName
      )
      this.frequentDriverForm.controls.salutation.setValue(
        this.data.mostFrequentDriver.salutation
      )
    }
  }

  saveChanges (): void {
    this.saving = true
    const payload: VehicleFrequentDriverDTO = {
      firstName: this.frequentDriverForm.get('firstName').value,
      lastName: this.frequentDriverForm.get('lastName').value,
      salutation: this.frequentDriverForm.get('salutation').value
    }

    const request$ = this.data.mostFrequentDriver
      ? this.vehicleService.updateFrequentDriver(this.data.ownershipId, payload)
      : this.vehicleService.createFrequentDriver(this.data.ownershipId, payload)

    request$.subscribe({
      next: value => {
        this.saving = false
        this.dialogRef.close({ success: true, value })
      },
      error: () => {
        this.saving = false
        this.dialogRef.close({ success: false })
      }
    })
  }
}
